/**
 * Cor do tema do evento. A parametrização traz uma cor só (`themeColor`) e daqui
 * sai a escala inteira: os tokens `--color-brand-*` que o `@theme` de
 * `index.css` define como padrão e que o Tailwind usa em toda classe `brand`.
 *
 * A escala é derivada com `color-mix` em vez de valores calculados aqui: o
 * navegador faz a mistura no espaço certo, e uma cor nova não exige recalcular
 * nada — a mesma abordagem do site de vendas.
 */
const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Cada passo como uma mistura da cor base com branco (tons claros) ou preto
 * (escuros). As proporções seguem a distância entre os passos da paleta padrão,
 * que varia só a luminosidade do mesmo matiz.
 */
const SCALE: Array<[token: string, percentage: number, mixWith: string]> = [
  ['--color-brand-50', 6, '#ffffff'],
  ['--color-brand-100', 12, '#ffffff'],
  ['--color-brand-200', 26, '#ffffff'],
  ['--color-brand-300', 44, '#ffffff'],
  ['--color-brand-400', 66, '#ffffff'],
  ['--color-brand-500', 84, '#ffffff'],
  ['--color-brand-600', 100, '#ffffff'],
  ['--color-brand-700', 84, '#000000'],
  ['--color-brand-800', 70, '#000000'],
  ['--color-brand-900', 60, '#000000'],
  ['--color-brand-950', 42, '#000000'],
];

/**
 * Texto, fundo e linha não são cinza puro na paleta padrão: são cinzas frios,
 * do mesmo matiz do azul da marca. Trocar só os tokens `brand` deixava a tela
 * azulada por baixo de uma marca de outra cor — a parte que "não mudou".
 *
 * Aqui cada um é uma pitada da cor sobre o **cinza neutro de mesma
 * luminosidade** do valor original, e não sobre o valor original: misturar com o
 * azul manteria o azul dominante. O cinza é quem carrega a luminosidade, então o
 * contraste de texto e linha continua o mesmo com qualquer cor — inclusive uma
 * bem clara, que sozinha deixaria linha e texto ilegíveis.
 */
const NEUTRALS: Array<[token: string, percentage: number, mixWith: string]> = [
  ['--color-ink', 10, '#121212'],
  ['--color-ink-muted', 18, '#525252'],
  ['--color-ink-subtle', 22, '#949494'],
  ['--color-surface-muted', 5, '#f8f8f8'],
  ['--color-surface-subtle', 8, '#f2f2f2'],
  ['--color-line', 12, '#e9e9e9'],
  ['--color-line-strong', 18, '#d8d8d8'],
];

export function isThemeColor(value: unknown): boolean {
  return HEX_COLOR.test(String(value ?? '').trim());
}

type Rgb = { r: number; g: number; b: number };

function parseHex(hex: string): Rgb {
  const value = hex.trim().slice(1);
  const full = value.length === 3 ? value.replace(/./g, digit => digit + digit) : value;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

const toHex = ({ r, g, b }: Rgb): string => `#${[r, g, b].map(channel => Math.round(channel).toString(16).padStart(2, '0')).join('')}`;

/** Mistura em sRGB, como o `color-mix` do CSS: `ratio` é quanto entra de `over`. */
const mix = (color: Rgb, over: Rgb, ratio: number): Rgb => ({
  r: color.r * (1 - ratio) + over.r * ratio,
  g: color.g * (1 - ratio) + over.g * ratio,
  b: color.b * (1 - ratio) + over.b * ratio,
});

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

/** Luminância relativa (WCAG 2.1), que é a base do cálculo de contraste. */
function luminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const scaled = value / 255;
    return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

const contrast = (first: Rgb, second: Rgb): number => {
  const one = luminance(first);
  const other = luminance(second);
  return (Math.max(one, other) + 0.05) / (Math.min(one, other) + 0.05);
};

const AA_CONTRAST = 4.5;

type Hsl = { h: number; s: number; l: number };

function toHsl({ r, g, b }: Rgb): Hsl {
  const [red, green, blue] = [r / 255, g / 255, b / 255];
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) return { h: 0, s: 0, l };

  const s = delta / (1 - Math.abs(2 * l - 1));
  const h = max === red ? ((green - blue) / delta) % 6 : max === green ? (blue - red) / delta + 2 : (red - green) / delta + 4;

  return { h: (h * 60 + 360) % 360, s, l };
}

function toRgb({ h, s, l }: Hsl): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [red, green, blue] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];

  return { r: (red + m) * 255, g: (green + m) * 255, b: (blue + m) * 255 };
}

/** Escurece ou clareia mexendo só na luminosidade: matiz e saturação ficam. */
const shift = (color: Rgb, amount: number): Rgb => {
  const { h, s, l } = toHsl(color);
  return toRgb({ h, s, l: Math.min(1, Math.max(0, l + amount)) });
};

/**
 * Texto sobre uma superfície na cor da marca — o cartão do login.
 *
 * É a própria cor, só com a luminosidade puxada até passar de 4,5:1 sobre esse
 * fundo. Não é preto nem branco: o preto sobre um cartão rosa lê como texto de
 * outro tema colado ali, enquanto o mesmo matiz bem escuro pertence à cor. E não
 * é mistura com preto/branco em sRGB, que lava a saturação e devolve um cinza
 * sujo — mexer na luminosidade do HSL preserva a cor.
 *
 * Tenta os dois sentidos e fica com o que chegar ao contraste; escurecer tem
 * preferência, por ser o que mais parece "a cor" num cartão claro. Preto ou
 * branco puros só quando nenhum dos dois chega lá.
 */
function readableOver(background: Rgb, base: Rgb): string {
  const attempt = (direction: number): Rgb | null => {
    let color = base;
    for (let step = 0; step < 24; step += 1) {
      if (contrast(color, background) >= AA_CONTRAST) return color;
      color = shift(color, direction * 0.04);
    }
    return contrast(color, background) >= AA_CONTRAST ? color : null;
  };

  const darker = attempt(-1);
  if (darker) return toHex(darker);

  const lighter = attempt(1);
  if (lighter) return toHex(lighter);

  return contrast(background, BLACK) >= contrast(background, WHITE) ? '#111111' : '#ffffff';
}

/**
 * A marca usada como **texto sobre fundo claro**: escurece em passos até chegar
 * aos 4,5:1 do WCAG AA. Uma cor clara continua pintando botão e fundo, mas como
 * texto ela seria ilegível — e é assim que o nome do evento e os links deixam de
 * depender de o operador ter escolhido uma cor escura.
 */
function readableAsText(base: Rgb): string {
  let color = base;
  for (let step = 0; step < 20 && contrast(color, WHITE) < AA_CONTRAST; step += 1) {
    color = mix(color, BLACK, 0.05);
  }
  return toHex(color);
}

/**
 * Aplica a cor do evento na página. Cor ausente ou inválida não mexe em nada: os
 * tokens seguem com o padrão do CSS, e um valor errado na parametrização não
 * derruba a identidade visual do painel.
 */
export function applyThemeColor(color: unknown): boolean {
  if (!isThemeColor(color)) return false;

  const base = String(color).trim();
  const root = document.documentElement;
  [...SCALE, ...NEUTRALS].forEach(([token, percentage, mixWith]) => {
    root.style.setProperty(token, percentage === 100 ? base : `color-mix(in srgb, ${base} ${percentage}%, ${mixWith})`);
  });

  // O anel de foco vinha com o azul da marca antiga escrito à mão no `@theme`:
  // sem isto, todo campo focado piscaria na cor anterior.
  root.style.setProperty('--shadow-focus', `0 0 0 4px color-mix(in srgb, ${base} 28%, transparent)`);

  const rgb = parseHex(base);
  // O cartão do login é um gradiente que vai do passo 200 ao 500; o 400 é o meio
  // dele, e é a superfície contra a qual o texto de lá precisa ter contraste.
  root.style.setProperty('--color-on-brand', readableOver(mix(rgb, WHITE, 0.34), rgb));
  root.style.setProperty('--color-brand-ink', readableAsText(rgb));

  return true;
}
